import { useMemo } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColorAt } from '../../themes/chartThemeColors';
import {
  calculateTotalAssetsUSDFormatted,
  calculateTotalAssetsInCurrencyFormatted,
  calculateNetAnnualRentYieldUSDFormatted,
  calculateNetAnnualRentYieldInCurrencyFormatted,
  calculateAverageTTMROIFormatted,
  calculateRetainedEarningsUSDFormatted,
  calculateRetainedEarningsInCurrencyFormatted,
  generateAllPortfolioCompositionData,
  calculatePortfolioSummaryStats,
  formatPortfolioSummaryTags,
  portfolioStatementAnchor,
  calculatePortfolioExtendedKpiMetrics,
  formatCompactCurrency,
  formatPercentValue,
  type PeriodMode,
  type PortfolioStatementSnapshot,
} from '../../functions/calculations';
import { convertUSDToCurrency } from '../../functions/calculations/currencyConversion';
import { buildMapPropertyFromUnit } from '../../components/maps/mapPropertyTooltipData';
import type { Unit, ClientOverview } from '../../types/api';
import type { LocationData, OverviewKpiState } from './types';
import { usePortfolioOverview } from './usePortfolioOverview';

function formatCapitalAtRiskFootnote(propertyCount: number): string {
  return `${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'}`;
}

interface OverviewKpiSource {
  overviewData: ClientOverview[];
  allUnits: Unit[];
  snapshot: PortfolioStatementSnapshot;
}

const emptyKpis: OverviewKpiState = {
  portfolioValue: '0',
  netAnnualRentYield: '0',
  ttmRoi: '0%',
  undistributedEarnings: '0',
  netEquity: '—',
  ttmCashReceived: '—',
  portfolioCoc: '—',
  portfolioNoiYield: '—',
  capitalAtRisk: '—',
  capitalAtRiskFootnote: '',
  loading: false,
  mapLocations: [],
  composition: {
    location: [],
    currency: [],
    operatingModel: [],
  },
  portfolioSummaryTags: [],
  overviewData: [],
  allUnits: [],
  periodAnchorKey: null,
};

export function useOverviewKpis(periodMode: PeriodMode): OverviewKpiState {
  const { theme } = useTheme();
  const { selectedClient, clientUnits, selectedCurrency, currencies } = useFilter();
  const overview = usePortfolioOverview();
  const source: OverviewKpiSource | null = overview.data
    ? {
        overviewData: overview.data.client_overview,
        allUnits: overview.data.units,
        snapshot: {
          balanceSheetData: overview.data.balance_sheet,
          cashflowData: overview.data.cashflow,
        },
      }
    : null;
  const loading = overview.loading;

  return useMemo(() => {
    if (!source || source.overviewData.length === 0) {
      return { ...emptyKpis, loading };
    }

    const { overviewData, allUnits, snapshot } = source;
    const targetCurrencyCode = selectedCurrency?.currency || 'USD';
    const needsConversion = targetCurrencyCode !== 'USD' && currencies.length > 0;

    const composition = generateAllPortfolioCompositionData(
      overviewData,
      allUnits,
      clientUnits,
      targetCurrencyCode,
      currencies,
    );

    const mapLocations = overviewData
      .map((overview: ClientOverview) => {
        const unit = allUnits.find((entry) => entry.unit_id_no === overview.unit_id_no);
        if (!unit?.latitude || !unit?.longitude) return null;

        const assetValue = overview.total_assets_usd || 0;
        const convertedValue = needsConversion
          ? convertUSDToCurrency(assetValue, targetCurrencyCode, currencies)
          : assetValue;

        return {
          id: `unit-${overview.unit_id_no}`,
          lat: unit.latitude,
          lng: unit.longitude,
          value: convertedValue,
          label: `${unit.city || ''}${unit.city && unit.country ? ', ' : ''}${unit.country || `Unit ${overview.unit_id_no}`}`.trim(),
          color: getChartColorAt(theme, overview.unit_id_no),
          property: buildMapPropertyFromUnit(unit),
        } as LocationData;
      })
      .filter((location): location is LocationData => location !== null);

    const portfolioSummaryStats = calculatePortfolioSummaryStats(
      clientUnits,
      allUnits,
      overviewData,
    );

    const extendedKpis = calculatePortfolioExtendedKpiMetrics(
      snapshot,
      overviewData,
      allUnits,
      targetCurrencyCode,
      currencies,
      periodMode,
    );

    return {
      portfolioValue: needsConversion
        ? calculateTotalAssetsInCurrencyFormatted(overviewData, targetCurrencyCode, currencies)
        : calculateTotalAssetsUSDFormatted(overviewData),
      netAnnualRentYield: needsConversion
        ? calculateNetAnnualRentYieldInCurrencyFormatted(overviewData, targetCurrencyCode, currencies)
        : calculateNetAnnualRentYieldUSDFormatted(overviewData),
      ttmRoi: calculateAverageTTMROIFormatted(overviewData),
      undistributedEarnings: needsConversion
        ? calculateRetainedEarningsInCurrencyFormatted(overviewData, targetCurrencyCode, currencies)
        : calculateRetainedEarningsUSDFormatted(overviewData),
      netEquity: formatCompactCurrency(extendedKpis.netEquity, targetCurrencyCode),
      ttmCashReceived: formatCompactCurrency(extendedKpis.ttmCashReceived, targetCurrencyCode),
      portfolioCoc: formatPercentValue(extendedKpis.portfolioCoc),
      portfolioNoiYield: formatPercentValue(extendedKpis.portfolioNoiYield),
      capitalAtRisk: formatCompactCurrency(extendedKpis.capitalAtRisk, targetCurrencyCode),
      capitalAtRiskFootnote: formatCapitalAtRiskFootnote(extendedKpis.capitalAtRiskPropertyCount),
      loading,
      mapLocations,
      composition,
      portfolioSummaryTags: formatPortfolioSummaryTags(portfolioSummaryStats),
      overviewData,
      allUnits,
      periodAnchorKey: portfolioStatementAnchor(snapshot),
    };
  }, [source, loading, selectedCurrency, currencies, clientUnits, theme, periodMode]);
}
